import { type StateCreator } from 'zustand';

import { type AiModelConfig } from '../../api/aimodels/useAiModels';

export type ModelSelectionEntry = {
  selectedModel: AiModelConfig | null;
  isAuto: boolean;
  lastAccessed: number;
};

export type ModelSelectionSlice = {
  // State
  selections: Record<string, ModelSelectionEntry>;

  // Actions
  setSelectedModel: (resourceId: string, model: AiModelConfig) => void;
  setIsAuto: (resourceId: string, isAuto: boolean) => void;
  /**
   * Migrate the selection entry from `fromResourceId` to `toResourceId`.
   * Used when a draft canvas commits and the empty-string placeholder id is
   * replaced with the real id. No-op if the source is missing, from === to,
   * or the target already has an entry (more recent intent).
   */
  migrateSelection: (fromResourceId: string, toResourceId: string) => void;
};

const DEFAULT_ENTRY: ModelSelectionEntry = Object.freeze({ isAuto: true, lastAccessed: 0, selectedModel: null });

export const getSelectionForResource = (
  selections: Record<string, ModelSelectionEntry>,
  resourceId: string,
): ModelSelectionEntry => selections[resourceId] ?? DEFAULT_ENTRY;

export type CreateModelSelectionSlice = StateCreator<ModelSelectionSlice, [], [], ModelSelectionSlice>;

export const createModelSelectionSlice: CreateModelSelectionSlice = (set) => ({
  migrateSelection: (fromResourceId, toResourceId) =>
    set((state) => {
      if (fromResourceId === toResourceId) {
        return state;
      }

      const source = state.selections[fromResourceId];

      if (!source) {
        return state;
      }

      const { [fromResourceId]: _omitted, ...rest } = state.selections;

      // Target entry wins (more recent intent); just drop the stale draft.
      if (state.selections[toResourceId]) {
        return { selections: rest };
      }

      return {
        selections: {
          ...rest,
          [toResourceId]: { ...source, lastAccessed: Date.now() },
        },
      };
    }),
  selections: {},
  setIsAuto: (resourceId, isAuto) =>
    set((state) => ({
      selections: {
        ...state.selections,
        [resourceId]: {
          ...getSelectionForResource(state.selections, resourceId),
          isAuto,
          lastAccessed: Date.now(),
        },
      },
    })),
  setSelectedModel: (resourceId, model) =>
    set((state) => ({
      selections: {
        ...state.selections,
        [resourceId]: {
          ...getSelectionForResource(state.selections, resourceId),
          lastAccessed: Date.now(),
          selectedModel: model,
        },
      },
    })),
});
